import { useCallback, useEffect, useRef, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { MessageDeliveryStatus } from '@jema-manager/shared';
import { supabase } from '@shared/api/supabaseClient';

type UnreadCounts = {
  unreadMessages: number;
  unreadConversations: number;
};

type UseUnreadCountsOptions = {
  userId?: string;
  enabled?: boolean;
};

const UNREAD_STATUSES: MessageDeliveryStatus[] = [
  MessageDeliveryStatus.SENT,
  MessageDeliveryStatus.DELIVERED,
];

const debounce = (fn: () => void, delay = 300) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fn, delay);
  };
};

export function useUnreadCounts({
  userId,
  enabled = true,
}: UseUnreadCountsOptions = {}) {
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(userId ?? null);
  const [counts, setCounts] = useState<UnreadCounts>({ unreadMessages: 0, unreadConversations: 0 });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const debouncedRefreshRef = useRef<() => void>(undefined);

  // Resolve user id from Supabase if not provided
  useEffect(() => {
    if (userId) {
      setResolvedUserId(userId);
      return;
    }

    supabase.auth.getUser().then(({ data }) => {
      setResolvedUserId(data.user?.id ?? null);
    });
  }, [userId]);

  const refreshCounts = useCallback(async () => {
    if (!enabled || !resolvedUserId) {
      setCounts({ unreadMessages: 0, unreadConversations: 0 });
      setLoading(false);
      return;
    }

    try {
      const { data, error: receiptsError } = await supabase
        .from('message_receipts')
        .select('message_id, messages!inner(conversation_id, deleted_at)', { count: 'exact' })
        .eq('recipient_id', resolvedUserId)
        .in('status', UNREAD_STATUSES)
        .is('messages.deleted_at', null);

      if (receiptsError) {
        throw receiptsError;
      }

      const conversationIds = new Set<string>();
      let unreadMessages = 0;

      (data as any[] ?? []).forEach((row: any) => {
        const conversationId = row.messages?.conversation_id;
        if (conversationId) {
          conversationIds.add(conversationId);
        }
        unreadMessages += 1;
      });

      setCounts({
        unreadMessages,
        unreadConversations: conversationIds.size,
      });
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(new Error(`Failed to load unread counts: ${message}`));
    } finally {
      setLoading(false);
    }
  }, [enabled, resolvedUserId]);

  useEffect(() => {
    debouncedRefreshRef.current = debounce(() => {
      void refreshCounts();
    }, 100); // Reduced from 250ms to 100ms for faster updates
  }, [refreshCounts]);

  useEffect(() => {
    const tearDownChannel = () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };

    if (!enabled) {
      setCounts({ unreadMessages: 0, unreadConversations: 0 });
      setLoading(false);
      tearDownChannel();
      return;
    }

    if (!resolvedUserId) {
      return;
    }

    void refreshCounts();

    const focusHandler = () => void refreshCounts();
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        void refreshCounts();
      }
    };
    window.addEventListener('focus', focusHandler);
    document.addEventListener('visibilitychange', visibilityHandler);

    tearDownChannel();

    const channel = supabase
      .channel(`unread-counts-${resolvedUserId}`)
      // Primary source of truth: receipts for this recipient
      // Listen to all events (INSERT, UPDATE, DELETE) on receipts
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_receipts',
          filter: `recipient_id=eq.${resolvedUserId}`,
        },
        () => {
          // Immediately refresh counts when receipts change
          debouncedRefreshRef.current?.();
        },
      )
      // Safety net: a new message insert should also refresh counts immediately
      // This catches new messages before receipts are created
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        (payload) => {
          // Only refresh if the message is not from the current user
          const message = payload.new as { sender_id: string | null };
          if (message.sender_id !== resolvedUserId) {
            // Small delay to allow receipt creation, then refresh
            setTimeout(() => {
              debouncedRefreshRef.current?.();
            }, 50);
          }
        },
      );

    channelRef.current = channel;

    const pollInterval = setInterval(() => {
      void refreshCounts();
    }, 30_000);

    // Re-sync on channel errors/timeouts
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        debouncedRefreshRef.current?.();
      }
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        void refreshCounts();
      }
    });

    return () => {
      window.removeEventListener('focus', focusHandler);
      document.removeEventListener('visibilitychange', visibilityHandler);
      clearInterval(pollInterval);
      tearDownChannel();
    };
  }, [enabled, refreshCounts, resolvedUserId]);

  return {
    unreadCounts: counts,
    loading,
    error,
  };
}
